/**
 * DataTable — canonical table primitive.
 *
 * Mockup parity: every table in `reignara_redesign/app/dashboard/page.tsx`
 * follows the same shell:
 *
 *   <div className="rounded-2xl border border-foreground/10 bg-card
 *                   shadow-sm overflow-x-auto">
 *     <table className="w-full min-w-[800px]">
 *       <thead>
 *         <tr className="border-b border-border bg-muted/50">
 *           <th className="px-4 py-3 text-left text-eyebrow-sm
 *                          text-muted-foreground">…</th>
 *         </tr>
 *       </thead>
 *       <tbody>
 *         <tr className="border-b border-border last:border-0">
 *           <td className="px-4 py-3 text-body text-foreground">…</td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </div>
 *
 * Pre-(m.6) admin tables hand-rolled this with `text-gold-700`
 * headers, `font-label` legacy classes, no rounded wrapper, and
 * inconsistent column padding. `<DataTable>` is the single way to
 * express the canonical shape — the wrapper, header, row treatment,
 * empty state, and minimum table width are all enforced.
 *
 * Generic over the row type so the renderCell callback receives
 * a properly-typed row argument.
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 14.1, 14.4, 18.5
 */

import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface DataTableColumn<TRow> {
  /** Stable column id — used as React key and for the th/td associations. */
  id: string;
  /** Visible header label (rendered in `text-eyebrow-sm` — 10px). */
  header: ReactNode;
  /** Render the cell content for a given row. The 0-indexed
   *  `rowIndex` is passed as the second argument so leaderboard
   *  tables can render rank numerals without stuffing a synthetic
   *  rank field on every row. */
  renderCell: (row: TRow, rowIndex: number) => ReactNode;
  /** Cell text alignment. Defaults to `left`. */
  align?: 'left' | 'right' | 'center';
  /** Optional explicit column width — accepts CSS sizes (`'120px'`,
   *  `'1fr'`, `'30%'`). When omitted, the column auto-sizes. */
  width?: string;
  /** Hide on mobile (< sm). Useful for secondary columns to keep
   *  the table readable at narrow viewports. */
  hideOnMobile?: boolean;
}

export interface DataTableProps<TRow> {
  /** Column definitions in display order. */
  columns: ReadonlyArray<DataTableColumn<TRow>>;
  /** Row data. */
  rows: ReadonlyArray<TRow>;
  /** Stable key extractor for each row. */
  rowKey: (row: TRow) => string;
  /**
   * Minimum table width in pixels. Below this width the wrapper
   * becomes horizontally scrollable. Match the mockup's
   * `min-w-[800px]` default for dense admin tables; pass a smaller
   * value (e.g. 560) for narrower tables.
   */
  minWidth?: number;
  /** Optional caption rendered above the wrapper (decorative). */
  caption?: ReactNode;
  /** Empty-state message when `rows` is empty. Renders inside the
   *  table wrapper without column structure. */
  emptyState?: ReactNode;
  /** Accessible label for the table — required for screen readers. */
  ariaLabel: string;
  /** Optional row-click handler. When provided, rows render with
   *  `cursor-pointer` and `role="button"` for accessibility. */
  onRowClick?: (row: TRow) => void;
  /** Optional `data-testid` factory for individual rows. */
  rowTestId?: (row: TRow) => string;
  /**
   * Optional per-row className factory. The returned classes are
   * merged onto the default row classes via `cn()`, so callers can
   * highlight a row (e.g. "this row is the current user") without
   * losing the canonical hover / border / transition classes.
   */
  rowClassName?: (row: TRow) => string | undefined;
  /**
   * Optional per-row `data-*` attribute factory. Useful for tests
   * that need to assert "the current user's row carries
   * data-is-me='true'".
   */
  rowDataAttrs?: (row: TRow) => Record<string, string | undefined>;
  /** Optional className passthrough on the wrapper. */
  className?: string;
  /** Override the wrapper's `data-testid` (default: `data-table-wrapper`). */
  'data-testid'?: string;
  /** Override the inner `<table>`'s `data-testid` (default: `data-table`). */
  'data-table-testid'?: string;
}

const ALIGN_CLASS: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  minWidth = 800,
  caption,
  emptyState,
  ariaLabel,
  onRowClick,
  rowTestId,
  rowClassName,
  rowDataAttrs,
  className,
  'data-testid': wrapperTestId,
  'data-table-testid': tableTestId,
}: DataTableProps<TRow>): JSX.Element {
  return (
    <div
      data-testid={wrapperTestId ?? 'data-table-wrapper'}
      className={cn(
        'rounded-card border border-border bg-card shadow-subtle',
        // Below `md` the wrapper renders the rows as a stacked card
        // list (see below). Above `md` it falls back to the canonical
        // horizontal-scroll table. The two are mutually exclusive
        // via Tailwind's responsive utilities so we keep a single
        // markup tree and let CSS swap visibility.
        'sm:overflow-x-auto',
        className,
      )}
    >
      {caption ? (
        <div
          data-testid="data-table-caption"
          className="border-b border-border px-5 py-4"
        >
          {caption}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div
          data-testid="data-table-empty"
          className="px-5 py-8 text-center font-sans text-body text-muted-foreground"
        >
          {emptyState ?? 'No data to display.'}
        </div>
      ) : (
        <>
          {/*
            Mobile rendering — below `md` (< 640px on Tailwind's `sm`
            breakpoint), the table is replaced by a card list. Each
            row becomes a `<div>` with the canonical
            `flex-col gap-3 p-4` shape from the mockup. Each cell
            renders as a label/value pair (label = column header,
            value = `renderCell` output). Columns marked
            `hideOnMobile` are hidden as before. The primary column
            (index 0) renders without a label since the row's
            "primary identity" line should anchor the card on its
            own.

            Spec: meridian-redesign — PR (c2) (mobile table card
            layout, mockup-binding to
            `reignara_redesign/app/dashboard/page.tsx`).
            Requirements: 14.8 (no horizontal overflow at 375px),
            14.9 (mobile-friendly table presentation).
          */}
          <div
            data-testid="data-table-mobile"
            className="sm:hidden divide-y divide-border"
          >
            {rows.map((row, rowIndex) => {
              const interactive = !!onRowClick;
              const handleRowClick = interactive
                ? () => onRowClick!(row)
                : undefined;
              const handleRowKeyDown = interactive
                ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick!(row);
                    }
                  }
                : undefined;
              const visibleCols = columns.filter((c) => !c.hideOnMobile);
              return (
                <div
                  key={rowKey(row)}
                  onClick={handleRowClick}
                  onKeyDown={handleRowKeyDown}
                  tabIndex={interactive ? 0 : undefined}
                  role={interactive ? 'button' : undefined}
                  {...(rowDataAttrs ? rowDataAttrs(row) : {})}
                  className={cn(
                    'p-4 flex flex-col gap-3 transition-colors min-h-[var(--touch-target-min)]',
                    interactive
                      ? 'hover:bg-muted/40 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/60'
                      : 'hover:bg-muted/30',
                    rowClassName ? rowClassName(row) : undefined,
                  )}
                >
                  {visibleCols.map((col, idx) => {
                    const cell = col.renderCell(row, rowIndex);
                    if (idx === 0) {
                      // Primary identity row — no label, full width.
                      return (
                        <div
                          key={col.id}
                          data-column-id={col.id}
                          className="font-sans text-body text-foreground font-medium"
                        >
                          {cell}
                        </div>
                      );
                    }
                    return (
                      <div
                        key={col.id}
                        data-column-id={col.id}
                        className="flex items-start justify-between gap-3"
                      >
                        <span className="text-eyebrow-xs text-muted-foreground shrink-0">
                          {col.header}
                        </span>
                        <span
                          className={cn(
                            'font-sans text-body text-foreground text-right',
                            ALIGN_CLASS[col.align ?? 'left'],
                          )}
                        >
                          {cell}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Desktop rendering — sm and above retains the canonical
              horizontal-scroll `<table>` from PR (m.6) Phase 1. */}
          <table
            data-testid={tableTestId ?? 'data-table'}
            aria-label={ariaLabel}
            className="w-full hidden sm:table"
            style={{ minWidth: `${minWidth}px` }}
          >
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {columns.map((col) => (
                  <th
                    key={col.id}
                    scope="col"
                    data-column-id={col.id}
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(
                      'px-4 py-3 text-eyebrow-xs uppercase tracking-[0.2em] text-primary font-bold',
                      ALIGN_CLASS[col.align ?? 'left'],
                      col.hideOnMobile ? 'hidden sm:table-cell' : undefined,
                    )}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const interactive = !!onRowClick;
                const handleRowClick = interactive
                  ? () => onRowClick!(row)
                  : undefined;
                const handleRowKeyDown = interactive
                  ? (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick!(row);
                      }
                    }
                  : undefined;
                return (
                  <tr
                    key={rowKey(row)}
                    data-testid={rowTestId ? rowTestId(row) : 'data-table-row'}
                    onClick={handleRowClick}
                    onKeyDown={handleRowKeyDown}
                    tabIndex={interactive ? 0 : undefined}
                    role={interactive ? 'button' : undefined}
                    {...(rowDataAttrs ? rowDataAttrs(row) : {})}
                    className={cn(
                      'border-b border-border last:border-0 transition-colors',
                      interactive
                        ? 'hover:bg-muted/40 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/60'
                        : 'hover:bg-muted/30',
                      rowClassName ? rowClassName(row) : undefined,
                    )}
                  >
                    {columns.map((col) => (
                      <td
                        key={col.id}
                        className={cn(
                          'px-4 py-3 font-sans text-body text-foreground',
                          ALIGN_CLASS[col.align ?? 'left'],
                          col.hideOnMobile ? 'hidden sm:table-cell' : undefined,
                        )}
                      >
                        {col.renderCell(row, rowIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export default DataTable;
